import React, { useEffect, useState } from 'react';
import { IntakeProvider, useIntake } from '../context/IntakeContext';
import { ProgressBar } from './ui/ProgressBar';
import { Button } from './ui/Button';
import { ChevronLeftIcon, ChevronRightIcon, AlertCircleIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { validateStep } from '../utils/validateStep';
import { Step1Welcome } from './steps/Step1Welcome';
import { Step2Contact } from './steps/Step2Contact';
import { Step3Team } from './steps/Step3Team';
import { Step4Company } from './steps/Step4Company';
import { Step5Story } from './steps/Step5Story';
import { Step6Legal } from './steps/Step6Legal';
import { Step7Branding } from './steps/Step7Branding';
import { Step8Goals } from './steps/Step8Goals';
import { Step9Type } from './steps/Step9Type';
import { Step10Pages } from './steps/Step10Pages';
import { Step11Content } from './steps/Step11Content';
import { Step12Tech } from './steps/Step12Tech';
import { Step13SEO } from './steps/Step13SEO';
import { Step14Planning } from './steps/Step14Planning';
import { Step15Summary } from './steps/Step15Summary';
const stepTitles = ['Welkom', 'Contactgegevens', 'Team', 'Bedrijfsgegevens', 'Verhaal & Positionering', 'Juridisch', 'Branding', 'Doelen', 'Type Website', "Pagina's", 'Content', 'Techniek', 'SEO', 'Planning', 'Samenvatting'];
function IntakeFormContent() {
  const {
    currentStep,
    setCurrentStep,
    totalSteps,
    data
  } = useIntake();
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showErrors, setShowErrors] = useState(false);
  // Validate current step whenever data changes
  useEffect(() => {
    const validation = validateStep(currentStep, data);
    setValidationErrors(validation.errors);
    setShowErrors(false); // Reset error display when data changes
  }, [currentStep, data]);
  const handleNext = () => {
    const validation = validateStep(currentStep, data);
    if (!validation.isValid) {
      setShowErrors(true);
      // Scroll to top to show errors
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    if (currentStep < totalSteps) {
      setShowErrors(false);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      setCurrentStep(currentStep + 1);
    }
  };
  const handlePrev = () => {
    if (currentStep > 1) {
      setShowErrors(false);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      setCurrentStep(currentStep - 1);
    }
  };
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1Welcome key="step1" data-id="element-1" data-mp="13yumnrs8hpub" />;
      case 2:
        return <Step2Contact key="step2" data-id="element-2" data-mp="zyu1loamqgea" />;
      case 3:
        return <Step3Team key="step3" data-id="element-3" data-mp="zzc2wzc5fkt2" />;
      case 4:
        return <Step4Company key="step4" data-id="element-4" data-mp="35qqzd4q515qo" />;
      case 5:
        return <Step5Story key="step5" data-id="element-5" data-mp="35tlmx54g76ob" />;
      case 6:
        return <Step6Legal key="step6" data-id="element-6" data-mp="31qyy7d32q1vc" />;
      case 7:
        return <Step7Branding key="step7" data-id="element-7" data-mp="2rlz1z7fle4fm" />;
      case 8:
        return <Step8Goals key="step8" data-id="element-8" data-mp="2rna83dwifo9w" />;
      case 9:
        return <Step9Type key="step9" data-id="element-9" data-mp="2vnc7thioq7mj" />;
      case 10:
        return <Step10Pages key="step10" data-id="element-10" data-mp="2vmsqa1wy5l91" />;
      case 11:
        return <Step11Content key="step11" data-id="element-11" data-mp="2hwz1fjlfz0zb" />;
      case 12:
        return <Step12Tech key="step12" data-id="element-12" data-mp="2hucu9l2gqnhm" />;
      case 13:
        return <Step13SEO key="step13" data-id="element-13" data-mp="2mu8povx3h6lk" />;
      case 14:
        return <Step14Planning key="step14" data-id="element-14" data-mp="27xzqtvh48v9o" />;
      case 15:
        return <Step15Summary key="step15" data-id="element-15" data-mp="27wgc49b3ysa0" />;
      default:
        return <Step1Welcome key="step1" data-id="element-16" data-mp="2cxm60agi8ll4" />;
    }
  };
  const showBottomNav = currentStep > 1 && currentStep < totalSteps;
  const hasErrors = validationErrors.length > 0;
  return <div className="min-h-screen bg-[#F7F8FA] text-gray-900" data-id="element-17" data-mp="1yznzfr8fdtg6">
      {currentStep > 1 && <ProgressBar currentStep={currentStep} totalSteps={totalSteps} title={stepTitles[currentStep - 1]} data-id="element-18" data-mp="23y93y2upmzaf" />}

      <main className={`px-4 pt-8 md:pt-12 ${showBottomNav ? 'pb-40' : 'pb-16'}`} data-id="element-19" data-mp="23zz5jxc3sna">
        <AnimatePresence mode="wait" data-id="element-20" data-mp="1y74pew8pk2vq">
          {showErrors && hasErrors && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="max-w-3xl mx-auto mb-6" data-id="element-21" data-mp="1t8tioudpm8hq">
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-xl" data-id="element-22" data-mp="1fb50jub11fgd">
                <div className="flex items-start" data-id="element-23" data-mp="1agim1i9nf6s2">
                  <AlertCircleIcon className="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-24" data-mp="15iqwguwh7wr1" />
                  <div data-id="element-25" data-mp="10l75qvildri7">
                    <h3 className="text-sm font-semibold text-red-800 mb-1" data-id="element-26" data-mp="10m0eu350wt3i">
                      Vul alle verplichte velden in
                    </h3>
                    <ul className="text-sm text-red-700 space-y-1" data-id="element-27" data-mp="rjzjxcx9ij50">
                      {validationErrors.map((error, index) => <li key={index} data-id="element-28" data-mp="336b4964qt22u" data-mpi={index}>• {error}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        <AnimatePresence mode="wait" data-id="element-29" data-mp="3c8khi3reb9jx">{renderStep()}</AnimatePresence>
      </main>

      {showBottomNav && <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 p-4 z-40 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]" data-id="element-30" data-mp="3h4rj5kcrdyw2">
          <div className="max-w-4xl mx-auto flex items-center justify-between" data-id="element-31" data-mp="2oj84stwv0jcv">
            <Button variant="outline" onClick={handlePrev} size="md" data-id="element-32" data-mp="2th1cjrdt9lf9">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-33" data-mp="2y7zu0uajcpk3" />
              Vorige
            </Button>
            <span className="text-xs text-gray-400 hidden sm:block" data-id="element-34" data-mp="20sm09cm2dh7m">
              {stepTitles[currentStep - 1]}
            </span>
            <Button onClick={handleNext} size="md" className={hasErrors && showErrors ? 'opacity-75' : ''} data-id="element-35" data-mp="25qx85hssthn7">
              Volgende
              <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-36" data-mp="3441gqxwip7nf" />
            </Button>
          </div>
        </div>}
    </div>;
}
export function IntakeForm() {
  return <IntakeProvider data-id="element-37" data-mp="3hwr2r2c1qckj">
      <IntakeFormContent data-id="element-38" data-mp="3dtkxny0o6rfn" />
    </IntakeProvider>;
}